import { CAROUSEL_DURATION_MS, isVODStatusFailed, MEDIA_TYPE } from '@/hooks/useTrailerViewMedia'
import { createPlaybackToken } from '@/server/services/blendvision'
import { Vod } from '@/server/services/types'
import { getFile, getVod } from '@/services/blendvision'
import { fetchTrailerMedia } from '@/services/cms'
import { TrailerViewVideoData } from '@/types/asset'
import useSWR from 'swr'

const buildPlayableVideoData = ({
  vod,
  download_uri,
  token,
  data,
}: {
  vod: Vod
  download_uri: string
  token: string | undefined
  data: { fileId: string; vodId: string }
}): TrailerViewVideoData => {
  return {
    fileId: data?.fileId,
    vodId: data?.vodId,
    url: download_uri || '',
    playbackToken: token || undefined,
    coverImage: vod?.cover_image?.auto?.url || '',
    vodStatus: vod?.status || '',
    isConverted: vod?.status === 'VOD_STATUS_SUCCEEDED',
    type: 'video',
  }
}

const buildImageData = (image: { url?: string }) => {
  return {
    url: image?.url || '',
    duration: CAROUSEL_DURATION_MS,
    type: 'image' as const,
  }
}

const convertInitialVideos = async (
  trailerVideos: Array<{ vod_id: string; file_id: string; cover_image?: string }>,
): Promise<TrailerViewVideoData[]> => {
  if (!Array.isArray(trailerVideos)) return []

  const videoData = await Promise.all(
    trailerVideos.map(async ({ vod_id, file_id }) => {
      const vodResponse = await getVod(vod_id)
      if (!('vod' in vodResponse)) {
        console.log(
          `[Trailer Media] Missing vod from Web Proxy: vod_id: ${vod_id}, file_id: ${file_id}`,
        )
        return undefined
      }
      const vod = vodResponse.vod
      const { download_uri } = await getFile(file_id)

      if (vod?.status === 'VOD_STATUS_SUCCEEDED') {
        const { token } = await createPlaybackToken({
          resourceId: vod_id,
          resourceType: 'RESOURCE_TYPE_VOD_EVENT',
        })
        return buildPlayableVideoData({
          vod,
          download_uri,
          token,
          data: { fileId: file_id, vodId: vod_id },
        })
      }
      if (!isVODStatusFailed(vod?.status)) {
        if (download_uri) {
          return buildPlayableVideoData({
            vod,
            download_uri,
            token: undefined,
            data: { fileId: file_id, vodId: vod_id },
          })
        }
      }
      return undefined
    }),
  )
  return videoData.filter((x): x is TrailerViewVideoData => Boolean(x))
}

const useInitialTrailerViewMedia = (roomId: string) => {
  // fetch trailer
  const { data: trailerMedia, isLoading: isMediaLoading } = useSWR(
    roomId ? `fetchTrailerMedia-useInitialTrailerViewMedia-${roomId}` : null,
    () => fetchTrailerMedia(roomId).then((res) => res?.results),
  )

  // Add video placeholder and key
  const videoSlotCount = trailerMedia?.trailer_videos?.length || 0
  const videoPlaceholders = Array.from({ length: videoSlotCount }).map((_, idx) => ({
    fileId: trailerMedia?.trailer_videos?.[idx]?.file_id || '',
    vodId: trailerMedia?.trailer_videos?.[idx]?.vod_id || '',
    url: '',
    playbackToken: undefined,
    coverImage: '',
    vodStatus: '',
    isConverted: false,
    type: MEDIA_TYPE.VIDEO,
    isPlaceholder: true,
    key: trailerMedia?.trailer_videos?.[idx]?.vod_id || `video-placeholder-${idx}`,
  }))

  // video convert
  const { data: videos = [], isLoading: isVideoLoading } = useSWR(
    trailerMedia?.trailer_videos && Array.isArray(trailerMedia.trailer_videos)
      ? ['convertInitialVideos', trailerMedia.trailer_videos]
      : null,
    () => convertInitialVideos(trailerMedia?.trailer_videos ?? []),
    { revalidateOnFocus: false },
  )

  // Use vod id to map and update isPlaceholder then returned video data
  const filledVideos = videoPlaceholders.map((placeholder, idx) => {
    const video = videos.find((v) => v.vodId === trailerMedia?.trailer_videos?.[idx]?.vod_id)
    if (video) {
      return {
        ...video,
        isPlaceholder: false,
        key: video.vodId || `video-${idx}`,
      }
    }
    return placeholder
  })

  // Add image placeholder and key
  const isImagesLoading = !trailerMedia?.trailer_images && isMediaLoading
  const defaultImagePlaceholderCount = 1
  const imageSlotCount = trailerMedia?.trailer_videos?.length || 0
  const imagePlaceholders =
    imageSlotCount > 0
      ? Array.from({ length: defaultImagePlaceholderCount }).map((_, idx) => ({
          url: '',
          duration: CAROUSEL_DURATION_MS,
          type: MEDIA_TYPE.IMAGE,
          isPlaceholder: true,
          key: `image-placeholder-${idx}`,
        }))
      : []
  // image convert
  const images =
    !isImagesLoading && Array.isArray(trailerMedia?.trailer_images)
      ? trailerMedia.trailer_images.map((img, idx) => ({
          ...buildImageData(img),
          isPlaceholder: false,
          key: img.url || `image-${idx}`,
        }))
      : imagePlaceholders

  const isLoading = isMediaLoading || isVideoLoading
  return {
    videos: filledVideos,
    images,
    isLoading,
    isMediaLoading,
    isVideoLoading,
  }
}

export default useInitialTrailerViewMedia
